<template>
  <div id="campaign_status">
    <CustomDropdown
      v-if="campaignDetails?.status?.toLowerCase() === 'in_progress'"
      max-height="200px"
      position="end"
    >
      <template #trigger>
        <ThemeImage
          class="min-h-10 min-w-[170px] cursor-pointer"
          :icon="
            callActive
              ? imageNames.IN_PROGRESS_GREEN_TAG
              : imageNames.IN_PROGRESS_PURPLE_TAG
          "
          alt="in_progress_tag"
        />
      </template>

      <template #default>
        <div class="flex flex-col">
          <div
            v-for="(item, index) in inProgressOptions"
            :key="`${item.id}_in_progress_option_${index}`"
            @click="item.handler(item)"
            class="dropdown_item"
          >
            <ThemeImage
              :icon="item.icon"
              class="size-[14px]"
              :alt="`${item.id}_icon`"
            />
            {{ item.text }}
          </div>
        </div>
      </template>
    </CustomDropdown>

    <a-button
      v-if="
        showButton && campaignDetails?.status?.toLowerCase() !== 'in_progress'
      "
      class="primary_large primary_large_dark"
      @click="handleButtonClick"
      :loading="isLoading"
      :disabled="isButtonDisabled"
    >
      <img
        :src="buttonConfig.icon"
        class="size-5"
        alt="icon"
        v-if="buttonConfig.icon"
      />
      <span v-if="buttonConfig.text">{{ buttonConfig.text }}</span>
    </a-button>

    <div
      v-if="!showButton"
      :class="[
        'flex flex-row gap-1 items-center h-10 px-4 rounded-[80px]',
        getTagContent.container,
      ]"
    >
      <ThemeImage
        :icon="getTagContent.icon"
        class="size-5 brightness-0 invert"
        :alt="`${getTagContent.text.toLowerCase()}_icon`"
      />

      <span class="text-base">{{ getTagContent.text }}</span>
    </div>

    <div id="campaign_status_content">
      <CustomModal
        :width="'32vw'"
        title="End Campaign?"
        v-model:visible="showEndModal"
      >
        <span class="text-sm"
          >Are you sure you want to end this campaign? You won't be able to
          resume it later</span
        >
        <template #footer>
          <div class="flex flx-row items-center gap-2">
            <a-button
              class="secondary_medium"
              @click="closeModal"
              :disabled="isLoading"
              >Cancel</a-button
            >

            <a-button
              class="primary_medium"
              @click="handleEndModalConfirm"
              :loading="isLoading"
              >Confirm</a-button
            >
          </div>
        </template>
      </CustomModal>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import {
  callCampaignStatuses,
  callCampaignButton,
} from "@/src/constants/callConstants.js";
import { statusTagMapping } from "@/src/constants/callCampaignConstants.js";
import {
  toUpdateCallCampaign,
  handleCampaignStatusUpdateUtils,
  toGetPhoneNumbers,
} from "@/src/utils/callsUtils.js";
import store from "@/src/store/index.js";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import eventBus from "@/src/utils/eventBus.js";

const props = defineProps({
  campaignDetails: { type: Object, required: true },
});

const isLoading = ref(false);
const selectedOption = ref(null);
const showEndModal = ref(false);
const userPhoneNumbers = ref(null);

const inProgressOptions = [
  {
    id: "pause",
    text: "Pause Campaign",
    value: callCampaignStatuses.PAUSED,
    icon: imageNames.PAUSE_GREY,
    handler: () => {
      if (!canUserPerformCampaignAction()) return;
      handleCampaignStatusUpdate(callCampaignStatuses.PAUSED);
    },
  },
  {
    id: "end",
    text: "End Campaign",
    value: callCampaignStatuses.ENDED,
    icon: imageNames.ENCLOSED_CROSS_GREY,
    handler: (data) => {
      if (!canUserPerformCampaignAction()) return;
      handleEndOptionClick(data);
    },
  },
];

const callActive = computed(() => store.getters["calls/isCallConnected"]);

const showButton = computed(() => {
  const status = props.campaignDetails?.status?.toLowerCase();
  return !["completed", "ended"].includes(status);
});

const buttonConfig = computed(() => {
  const status = props.campaignDetails?.status?.toLowerCase() || "draft";
  return callCampaignButton[status] || callCampaignButton.draft;
});

const getTagContent = computed(() => {
  const currentStatus = store.getters["callsCampaign/campaignStatus"];
  return statusTagMapping[currentStatus];
});

const isButtonDisabled = computed(() => {
  const currentCampaignStatus = store.getters["callsCampaign/campaignStatus"];
  const currentCampaignDetails = store.getters["callsCampaign/campaignDetails"];

  if (currentCampaignStatus === callCampaignStatuses.IN_PROGRESS) {
    return currentCampaignDetails?.id !== props.campaignDetails?.id;
  } else if (store.getters["calls/hasActiveCall"]) {
    return true;
  }
  return false;
});

const closeModal = () => {
  showEndModal.value = false;
};

const canUserPerformCampaignAction = () => {
  if (userPhoneNumbers.value === null) return true;

  const hasValidNumber =
    userPhoneNumbers.value?.length > 0 &&
    userPhoneNumbers.value.some((n) => n?.phone_number?.trim());
  if (!hasValidNumber) {
    message.error("Please get a phone number to make changes to the campaign.");
    return false;
  }
  return true;
};

const handleCampaignButtonClick = async () => {
  try {
    await store.dispatch(
      "callsCampaign/startCampaign",
      props.campaignDetails?.id,
    );
  } catch (error) {
    console.error("Error starting campaign:", error);
  }
};

const handleCampaignStatusUpdate = async (status) => {
  try {
    const payload = { status: status };
    const response = await toUpdateCallCampaign(
      props.campaignDetails?.id,
      payload,
    );

    if (response) {
      message.success(
        `Campaign ${status === callCampaignStatuses.IN_PROGRESS ? "resumed" : status}`,
      );
      if(status === callCampaignStatuses.IN_PROGRESS){
        await store.dispatch("callsCampaign/updateCampaignActive", true);
      }
      await handleCampaignStatusUpdateUtils(status);
    }
  } catch (error) {
    console.error("Error updating campaign status:", error);
  }
};

const handleEndOptionClick = async (data) => {
  try {
    selectedOption.value = data;
    showEndModal.value = true;
  } catch (error) {
    console.error("Error pausing campaign:", error);
  }
};

const handleEndModalConfirm = async () => {
  try {
    isLoading.value = true;
    await handleCampaignStatusUpdate(selectedOption.value?.value);
  } catch (error) {
    console.error(
      "An error occurred while handling pause or end modal confirm: ",
      error,
    );
  } finally {
    isLoading.value = false;
    showEndModal.value = false;
  }
};

const handleButtonClick = async () => {
  try {
    if (!canUserPerformCampaignAction()) return;

    isLoading.value = true;
    const action = buttonConfig.value.action;

    switch (action) {
      case "start":
        await handleCampaignButtonClick();
        break;
      case "pause":
        await handleCampaignStatusUpdate(callCampaignStatuses.PAUSED);
        eventBus.emit("stop-count-down");
        break;
      case "resume":
        await handleCampaignStatusUpdate(callCampaignStatuses.IN_PROGRESS);
        break;
      default:
        console.log("No action defined for this status");
    }
  } catch (error) {
    console.error("Error handling button click: ", error);
  } finally {
    isLoading.value = false;
  }
};

const fetchUserPhoneNumbers = async () => {
  try {
    isLoading.value = true;
    const phoneNumbers = await toGetPhoneNumbers({ user: true });
    userPhoneNumbers.value = phoneNumbers ?? null;
  } catch (error) {
    console.error("Error fetching user phone numbers:", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchUserPhoneNumbers();
});
</script>
